import {
    ComputeStatus,
    DataSourceWithConfigAndEtlConnection,
    DataSourceWithConfigAndEtlExtended,
    EtlInstance,
    EtlInstanceStatus,
    EtlInstanceType,
    resource_memory_cost_per_gb_per_hour,
    resource_storage_cost_per_gb_per_hour,
} from "@biggeo/bg-server-lib/studio";
import { GridColDef } from "@biggeo/bg-ui";
import {
    AvatarGroup,
    avatarListClasses,
    Box,
    Button,
    Chip,
    ColFilterType,
    DataCloudSourceWrapper,
    DataGridContainer,
    DataSourceStatus,
    getFileTypeIcon,
    Grid,
    IconAvatar,
    IFilterSearchPaginate,
    MemoizedField,
    OverflowingTypography,
    Stack,
    Tooltip,
    Typography,
} from "@biggeo/bg-ui/lab";
import {
    ArrowSplitOutline,
    BigGeoLogo,
    CheckCircle,
    Info,
    PublicOutline,
    SyncOutline,
} from "@biggeo/bg-ui/lab/icons";
import {
    convertByteSize,
    formatDateMMMDYYYY,
    formatNumberWithPrecision,
    getFileTypeLabel,
    toByteType,
} from "@biggeo/bg-utils";
import bytes from "bytes";
import startCase from "lodash/startCase";
import compact from "lodash/compact";
import maxBy from "lodash/maxBy";
import sum from "lodash/sum";
import { useNavigate } from "react-router";

import { Routes } from "../../navigation/redux/model";
import { IMyDataContainer } from "../containers/MyDataContainer";
import MyDataEmptyView from "./MyDataEmptyView";
import { ComputeToggleContainer } from "../containers/ComputeToggleContainer";

interface IMyDataView extends IMyDataContainer {
    data: DataSourceWithConfigAndEtlConnection;
    pagination: IFilterSearchPaginate;
    loading: boolean;
    onEnableHotCompute: (data: DataSourceWithConfigAndEtlExtended) => void;
    onDisableHotCompute: (data: DataSourceWithConfigAndEtlExtended) => void;
    toIndexingPage: (dataSourceId: string) => void;
}

const tooltips: {
    compute: string;
    status: string;
    exportData: string;
    resources: string;
    indexed: string;
    useDataset: string;
} = {
    compute:
        "Turn on hot compute to enable instant access to your dataset for Datascape use",
    status: "Dataset compute state",
    exportData: "Export data to your cloud storage",
    resources:
        "Hot compute enabled datasets will be immediately available for use without loading times in Datascape",
    indexed: "Dataset successfully indexed by BigGeo",
    useDataset: "Add this dataset to a Map Template",
};

const MyDataView = ({
    data,
    pagination,
    loading,
    onAddOnDatalabClick,
    onEnableHotCompute,
    onDisableHotCompute,
    toIndexingPage,
}: IMyDataView) => {
    const toPage = useNavigate();

    const isIndexed = (instance?: EtlInstance): boolean => {
        const etlStatus = instance?.status;
        const etlType = instance?.type;

        return (
            !!etlStatus &&
            etlStatus === EtlInstanceStatus.Completed &&
            !!etlType &&
            etlType === EtlInstanceType.IndexDataset
        );
    };

    const columns: GridColDef<DataSourceWithConfigAndEtlExtended>[] = [
        {
            field: "source",
            headerName: "Source",
            width: 100,
            sortable: false,
            filterable: false,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                return (
                    <MemoizedField>
                        <DataCloudSourceWrapper
                            cloudInfo={
                                params.row.etlDatasetLocation
                                    ?.cloudStorageInfo || undefined
                            }
                            fileType={params.row.etlDatasetLocation?.fileType}
                            cronInterval={
                                params.row.etlDatasetLocation?.cronInterval ||
                                undefined
                            }
                        />
                    </MemoizedField>
                );
            },
        },
        {
            field: "name",
            headerName: "Name",
            minWidth: 200,
            flex: 1,
            sortable: false,
            type: ColFilterType.string,
            filterable: true,
            headerAlign: "left",
            align: "center",
            renderCell: (params) => (
                <MemoizedField
                    title={params.row.dataSource.name}
                    subTitle={params.row.dataSource.description || undefined}
                />
            ),
        },
        {
            field: "lastUpdate",
            headerName: "Last Update",
            flex: 1,
            minWidth: 140,
            sortable: false,
            filterable: false,
            type: ColFilterType.string,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                const updates = compact([
                    params.row.dataSource.updatedAt,
                    params.row.etlDatasetLocation?.updatedAt,
                    params.row.latestEtlInstance?.updatedAt,
                    params.row.configuration.updatedAt,
                ]);

                const lastDate =
                    maxBy(updates, (d) => d) || params.row.dataSource.updatedAt;

                return (
                    <Grid container alignItems="center" justifyContent="center">
                        <Typography variant="body3" fontWeight="semibold">
                            {formatDateMMMDYYYY(lastDate)}
                        </Typography>
                    </Grid>
                );
            },
        },
        {
            field: "exportData",
            headerName: "Export Data",
            flex: 1,
            minWidth: 140,
            filterable: false,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderHeader: (params) => (
                <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Typography variant="body3" fontWeight="semibold">
                        {startCase(params.field)}
                    </Typography>
                    <Tooltip title={tooltips.exportData} sx={{ maxWidth: 52 }}>
                        <Box>
                            <Info color="primary" size="xxs" />
                        </Box>
                    </Tooltip>
                </Stack>
            ),
            renderCell: ({ row }) => {
                const status = row.configuration?.status;

                const disabled =
                    !isIndexed(row.latestEtlInstance || undefined) ||
                    status !== ComputeStatus.hot;

                return (
                    <MemoizedField disable={disabled}>
                        <Button
                            variant="outlined"
                            endNode={<ArrowSplitOutline size="xs" />}
                            disabled={disabled}
                            onClick={() => {
                                toPage(
                                    `${Routes.myData}/egress/${row.dataSource.id}`
                                );
                            }}
                        >
                            Egress Data
                        </Button>
                    </MemoizedField>
                );
            },
        },
        {
            field: "useDataset",
            headerName: "Use Dataset",
            flex: 1,
            minWidth: 140,
            filterable: false,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderHeader: () => (
                <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Typography variant="body3" fontWeight="semibold">
                        Use Dataset
                    </Typography>
                    <Tooltip title={tooltips.useDataset} sx={{ maxWidth: 52 }}>
                        <Box>
                            <Info color="primary" size="xxs" />
                        </Box>
                    </Tooltip>
                </Stack>
            ),
            renderCell: ({ row }) => {
                const status = row.configuration?.status;

                const disabled =
                    !isIndexed(row.latestEtlInstance || undefined) ||
                    status !== ComputeStatus.hot;

                return (
                    <MemoizedField disable={disabled}>
                        <Button
                            variant="outlined"
                            endNode={<PublicOutline size="xs" />}
                            disabled={disabled}
                            onClick={() => {
                                toPage(
                                    row.userDataSource
                                        ? `${Routes.mapTemplatesCreate}/${row.userDataSource.id}`
                                        : `${Routes.mapTemplatesCreate}`
                                );
                            }}
                        >
                            Create Map
                        </Button>
                    </MemoizedField>
                );
            },
        },
        {
            field: "compute",
            headerName: "Compute",
            minWidth: 140,
            flex: 1,
            filterable: false,
            sortable: false,
            type: ColFilterType.boolean,
            headerAlign: "center",
            renderHeader: (params) => (
                <Stack
                    flexDirection="row"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                >
                    <Typography variant="body3" fontWeight="semibold">
                        {startCase(params.field)}
                    </Typography>
                    <Tooltip title={tooltips.compute} sx={{ maxWidth: 52 }}>
                        <Box>
                            <Info color="primary" size="xxs" />
                        </Box>
                    </Tooltip>
                </Stack>
            ),
            renderCell: (params) => {
                return (
                    <ComputeToggleContainer
                        datasourceWithConfig={params.row}
                        onClick={() => onDisableHotCompute(params.row)}
                        updateLoading={false}
                    />
                );
            },
        },
        {
            field: "resources",
            headerName: "Resources Usage",
            minWidth: 160,
            flex: 1,
            filterable: false,
            sortable: false,
            type: ColFilterType.boolean,
            headerAlign: "center",
            align: "center",
            renderHeader: () => (
                <Stack
                    flexDirection="row"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                >
                    <Typography variant="body3" fontWeight="semibold">
                        Resources Usage
                    </Typography>
                    <Tooltip title={tooltips.resources} sx={{ maxWidth: 52 }}>
                        <Box>
                            <Info color="primary" size="xxs" />
                        </Box>
                    </Tooltip>
                </Stack>
            ),
            renderCell: (params) => {
                const isHot =
                    params.row.configuration.status === ComputeStatus.hot;

                const storageActual = convertByteSize(
                    toByteType(
                        Number(params.row.dataSource.diskActual) || 0,
                        "bytes"
                    ),
                    "gigabytes"
                );
                const memActual = convertByteSize(
                    toByteType(
                        Number(params.row.dataSource.ramActual) || 0,
                        "bytes"
                    ),
                    "gigabytes"
                );

                const creditsPerHour =
                    resource_storage_cost_per_gb_per_hour *
                        storageActual.value +
                    resource_memory_cost_per_gb_per_hour * memActual.value;

                return (
                    <Grid container justifyContent="center" alignItems="center">
                        {isHot ? (
                            <Stack gap={0.5} alignItems="flex-start">
                                <Stack flexDirection="row" alignItems="center">
                                    <Typography
                                        variant="body4"
                                        fontWeight="semibold"
                                    >
                                        {`${formatNumberWithPrecision(creditsPerHour)} Credits`}
                                    </Typography>
                                    <Typography
                                        variant="body4"
                                        fontWeight="semibold"
                                        textColor="disabled.onContainer"
                                    >
                                        /hourly use
                                    </Typography>
                                </Stack>
                                <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Box
                                        sx={{
                                            width: "6px",
                                            height: "6px",
                                            borderRadius: "100%",
                                            backgroundColor: (theme) =>
                                                theme.palette.info.onContainer,
                                        }}
                                    />
                                    <Typography
                                        variant="body4"
                                        fontWeight="semibold"
                                        textColor="disabled.onContainer"
                                    >
                                        Added to used balance
                                    </Typography>
                                </Stack>
                            </Stack>
                        ) : (
                            <Button
                                rounded
                                variant="outlined"
                                disabled={
                                    params.row.configuration.status ===
                                        ComputeStatus.loading ||
                                    !params.row.dataSource.collectionName
                                }
                                startNode={<SyncOutline />}
                                onClick={() => onEnableHotCompute(params.row)}
                            >
                                Enable
                            </Button>
                        )}
                    </Grid>
                );
            },
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 140,
            filterable: true,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderHeader: (params) => (
                <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Typography variant="body3" fontWeight="semibold">
                        {startCase(params.field)}
                    </Typography>
                    <Tooltip title={tooltips.status} sx={{ maxWidth: 52 }}>
                        <Box>
                            <Info color="primary" size="xxs" />
                        </Box>
                    </Tooltip>
                </Stack>
            ),
            renderCell: (params) => {
                const status = params.row.configuration.status;

                return (
                    <MemoizedField>
                        <DataSourceStatus status={status} />
                    </MemoizedField>
                );
            },
        },
        {
            field: "fileType",
            headerName: "File Type",
            flex: 1,
            minWidth: 165,
            filterable: false,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                const fileType = row.etlDatasetLocation?.fileType;

                if (!fileType) return <MemoizedField title="-" />;

                const icon = getFileTypeIcon(fileType);
                const label = getFileTypeLabel(fileType);

                if (isIndexed(row.latestEtlInstance || undefined)) {
                    return (
                        <MemoizedField>
                            <Stack
                                flexDirection="row"
                                alignItems="center"
                                gap={4}
                            >
                                <AvatarGroup
                                    max={2}
                                    size="xs"
                                    sx={{
                                        [`& .${avatarListClasses.avatar}`]: {
                                            border: 1,
                                            borderColor: (theme) =>
                                                theme.palette.stroke[100],
                                        },
                                    }}
                                >
                                    {icon}
                                    <IconAvatar
                                        size="xs"
                                        variant="filled"
                                        color="background"
                                    >
                                        <BigGeoLogo color="primary" size="xs" />
                                    </IconAvatar>
                                </AvatarGroup>
                                <OverflowingTypography
                                    variant="body3"
                                    fontWeight="semibold"
                                >
                                    {`${label} & Index`}
                                </OverflowingTypography>
                            </Stack>
                        </MemoizedField>
                    );
                }

                return (
                    <MemoizedField>
                        <Stack flexDirection="row" alignItems="center" gap={4}>
                            {icon}
                            <OverflowingTypography
                                variant="body3"
                                fontWeight="semibold"
                            >
                                {`${label}`}
                            </OverflowingTypography>
                        </Stack>
                    </MemoizedField>
                );
            },
        },
        {
            field: "indexed",
            headerName: "Indexed",
            flex: 1,
            minWidth: 86,
            filterable: false,
            sortable: false,
            type: ColFilterType.boolean,
            headerAlign: "center",
            align: "center",
            renderHeader: (params) => (
                <Stack
                    flexDirection="row"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                >
                    <Typography variant="body3" fontWeight="semibold">
                        {startCase(params.field)}
                    </Typography>
                    <Tooltip title={tooltips.indexed} sx={{ maxWidth: 52 }}>
                        <Box>
                            <Info color="primary" size="xxs" />
                        </Box>
                    </Tooltip>
                </Stack>
            ),
            renderCell: ({ row }) => {
                return (
                    <Grid container justifyContent="center" alignItems="center">
                        {isIndexed(row.latestEtlInstance || undefined) ? (
                            <CheckCircle color="success" size="xxs" />
                        ) : (
                            <Chip
                                variant="outlined"
                                rounded
                                density="dense"
                                color="primary"
                                endNode={<BigGeoLogo />}
                                onClick={() => {
                                    toIndexingPage(row.dataSource.id);
                                }}
                            >
                                Index
                            </Chip>
                        )}
                    </Grid>
                );
            },
        },
        {
            field: "size",
            headerName: "Size",
            flex: 1,
            minWidth: 100,
            filterable: false,
            type: ColFilterType.number,
            sortable: false,
            headerAlign: "left",
            align: "center",
            renderCell: (params) => {
                const diskActual =
                    Number(params.row.dataSource.diskActual) || 0;

                const originalFileSizes = sum(
                    params.row.dataSource.originalSourceInfo?.map(
                        (file) => file.size
                    ) || []
                );

                return (
                    <MemoizedField
                        title={`${bytes(diskActual ? diskActual : originalFileSizes)}`}
                        subTitle={`Index - ${bytes(Number(params.row.dataSource.ramActual) || 0)}`}
                    />
                );
            },
        },
        {
            field: "manage",
            align: "center",
            width: 124,
            filterable: false,
            sortable: false,
            headerName: "",
            renderCell: () => {
                return (
                    <MemoizedField>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                onAddOnDatalabClick();
                            }}
                        >
                            Manage
                        </Button>
                    </MemoizedField>
                );
            },
        },
    ];

    return (
        <Stack
            height="100%"
            width="100%"
            sx={{
                paddingTop: 4,
            }}
        >
            <DataGridContainer
                loading={loading}
                pinnedColumns={{ right: ["manage"] }}
                columns={columns}
                rows={data.data}
                getRowId={(r) => r.dataSource.id}
                rowCount={data.total}
                initialState={{
                    pinnedColumns: {
                        right: ["manage"],
                    },
                }}
                pagination
                filterSearchPaginateProps={pagination}
                enableColumnResize
                customNoRowsOverlay={<MyDataEmptyView />}
            />
        </Stack>
    );
};

export default MyDataView;
